import { useCallback, useEffect, useState } from 'react';

/**
 * Réglage de taille d'affichage, propre à l'application.
 *
 * ── Pourquoi pas simplement le zoom du navigateur ───────────────────────────
 * Il existe, mais il s'applique à tout le site et se perd d'un poste à l'autre.
 * Un cahier journal se consulte sur un portable de salle de classe, souvent
 * ancien et peu lumineux, puis se prépare sur un grand écran le soir : le
 * besoin de grossir n'est pas le même selon l'endroit, et le réglage doit
 * suivre l'écran, pas la personne.
 *
 * ── Il agit sur le CONTENU, pas sur la coquille ─────────────────────────────
 * Grossir la barre latérale et les en-têtes réduirait la place du tableau, ce
 * qui est l'inverse du but. Seul le contenu principal est mis à l'échelle.
 *
 * ── L'impression n'en tient aucun compte ────────────────────────────────────
 * La feuille de style d'impression remet l'échelle à 1 : une feuille A4 ne se
 * dimensionne pas au confort de lecture de l'écran.
 */

const CLE = 'journal.zoom';

/** Bornes et pas, en pourcentage. */
const MIN = 70;
const MAX = 160;
const PAS = 10;

/** Lecture du réglage mémorisé, bornée : une valeur aberrante rendrait l'application inutilisable. */
function lireZoom(): number {
  const brut = Number(globalThis.localStorage?.getItem(CLE));
  if (!Number.isFinite(brut) || brut <= 0) return 100;
  return Math.min(Math.max(Math.round(brut), MIN), MAX);
}

export function useZoom() {
  const [zoom, setZoom] = useState<number>(lireZoom);

  useEffect(() => {
    /*
     * Une variable CSS sur la racine plutôt qu'un style en ligne.
     *
     * Le contenu à mettre à l'échelle n'est pas un composant unique — c'est le
     * `<main>` de chaque coquille. Passer par une variable évite de faire
     * descendre la valeur à travers toute l'arborescence.
     */
    globalThis.document?.documentElement.style.setProperty('--zoom-contenu', String(zoom / 100));
    globalThis.localStorage?.setItem(CLE, String(zoom));
  }, [zoom]);

  const ajuster = useCallback((delta: number) => {
    setZoom((z) => Math.min(Math.max(z + delta, MIN), MAX));
  }, []);

  return { zoom, ajuster, reinitialiser: () => setZoom(100), MIN, MAX, PAS };
}

/**
 * Panneau flottant, en bas à droite.
 *
 * Discret par défaut : c'est un réglage qu'on pose une fois, pas un outil de
 * travail. Il ne s'imprime pas et s'efface sous le contenu au survol du bord.
 */
export function ReglageZoom() {
  const { zoom, ajuster, reinitialiser, MIN: min, MAX: max, PAS: pas } = useZoom();

  return (
    <div
      role="group"
      aria-label="Taille d'affichage"
      className={[
        'fixed bottom-3 right-3 z-40 flex items-center gap-0.5 rounded-lg',
        'border border-[var(--color-border)] bg-[var(--color-surface)] p-0.5',
        'shadow-[var(--ombre-flottante)] print:hidden',
      ].join(' ')}
    >
      <button
        type="button"
        onClick={() => ajuster(-pas)}
        disabled={zoom <= min}
        aria-label="Réduire la taille d'affichage"
        className="grid size-7 place-items-center rounded-md text-[15px] font-semibold text-[var(--color-fg)] hover:bg-[var(--color-surface-hover)] disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]"
      >
        −
      </button>

      {/*
        * Le pourcentage remet à 100 % d'un clic.
        *
        * Sans cette issue, revenir à la taille normale demande de compter les
        * appuis — et l'on s'arrête rarement pile au bon endroit.
        */}
      <button
        type="button"
        onClick={reinitialiser}
        aria-label={`Taille d'affichage : ${zoom} %. Revenir à 100 %`}
        className="min-w-[3.25rem] rounded-md px-1 py-1 text-[12px] tabular-nums text-[var(--color-fg-muted)] hover:bg-[var(--color-surface-hover)] focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]"
      >
        {`${zoom} %`}
      </button>

      <button
        type="button"
        onClick={() => ajuster(pas)}
        disabled={zoom >= max}
        aria-label="Agrandir la taille d'affichage"
        className="grid size-7 place-items-center rounded-md text-[15px] font-semibold text-[var(--color-fg)] hover:bg-[var(--color-surface-hover)] disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]"
      >
        +
      </button>
    </div>
  );
}
